/*
 * <recently-viewed-products> — populates the Recently Viewed section
 * (sections/recently-viewed-products.liquid) from the rv:handles localStorage
 * list that recently-viewed.js maintains on every product page.
 *
 * Cards are fetched per handle via the Section Rendering API
 * (?section_id=recently-viewed-product-card) so they are the exact same
 * card-product markup as the featured-collection "RECOMMENDED FOR YOU" grid.
 * The section stays hidden when the shopper has no history.
 *
 * The section reserves its final height BEFORE any card is fetched. The handle
 * list is in localStorage, so the number of cards this section will render is
 * known synchronously the moment the element upgrades — at DOMContentLoaded,
 * long before anyone has scrolled to the bottom of a ~4,700px product page.
 * It used to sit at display:none until every fetch resolved and then inject the
 * finished row in one go, which on a throttled phone landed after the shopper
 * had already reached the footer: a single measured layout shift of 0.46, which
 * is 4.6x the entire CLS budget for the visit. Reserving first turns that
 * injection into a fill of a box that is already the right size.
 */
(() => {
  if (customElements.get('recently-viewed-products')) return;

  const STORAGE_KEY = 'rv:handles';
  const CARD_HEIGHT_KEY = 'rv:cardHeight';
  const CARD_SECTION_ID = 'recently-viewed-product-card';
  // '/' on the main market, '/en-gb/' on a Markets subfolder. A root-path
  // render from a subfolder page would come back in US dollars.
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // Fallback only, for the case where no other product grid is on the page to
  // measure: portrait card media is 2:3, so the image box is 1.5x the column
  // width, and this covers the title/price row underneath it.
  const META_ALLOWANCE = 36;

  const normalizeHandle = (handle) => (handle || '').toString().trim().toLowerCase();

  const readHandles = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      const seen = new Set();
      const cleaned = [];
      parsed.forEach((entry) => {
        const handle = normalizeHandle(entry);
        if (!handle || seen.has(handle)) return;
        seen.add(handle);
        cleaned.push(handle);
      });
      return cleaned;
    } catch (_) {
      return [];
    }
  };

  // The height of one card in THIS rail, remembered from the last time it
  // rendered for real. Keyed by the grid's width so a phone and a desktop never
  // reuse each other's number, and so a breakpoint change invalidates it.
  //
  // This exists because the rail's own cards are not the same height as the
  // cards in the neighbouring "Recommended For You" grid -- measured 304px here
  // against 328px there at 390px wide, because this section renders its cards
  // with the image carousel switched off. Measuring a real card from this rail
  // is the only reading that is right by construction; the reference card is a
  // starting estimate for the very first visit, and is ~24px per row too tall.
  const readCachedCardHeight = (width) => {
    try {
      const parsed = JSON.parse(localStorage.getItem(CARD_HEIGHT_KEY) || 'null');
      if (!parsed || parsed.w !== width) return 0;
      return typeof parsed.h === 'number' && parsed.h > 0 ? parsed.h : 0;
    } catch (_) {
      return 0;
    }
  };

  const writeCachedCardHeight = (width, height) => {
    try {
      localStorage.setItem(CARD_HEIGHT_KEY, JSON.stringify({ w: width, h: height }));
    } catch (_) {
      /* private mode / quota: the reference-card estimate still applies */
    }
  };

  class RecentlyViewedProducts extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.grid = this.querySelector('[id^="Slider-"]');
      this.sliderComponent = this.querySelector('slider-component');
      this.sliderButtons = this.querySelector('.slider-buttons');
      this.titleWrapper = this.querySelector('.collection__title');

      if (!this.grid) return;

      // In the editor, load immediately so the merchant sees the section while
      // configuring it. Placeholders are server-rendered there, so there is
      // nothing to reserve.
      if (this.hasAttribute('data-design-mode')) {
        this.loadProducts();
        return;
      }

      this.handles = this.usableHandles();

      // No history: stay hidden and fetch nothing. This is the one path that
      // leaves the section with no box, and it is the correct one — there is
      // no row coming, so there is nothing to reserve space for.
      if (!this.handles.length) return;

      if (!this.reserveSpace()) {
        // Could not measure a height to reserve. Fall back to the old
        // reveal-on-load behaviour rather than showing an empty titled block.
        this.loadProducts();
        return;
      }

      // The space is committed, so the row MUST arrive — a reserved block that
      // never fills is a permanent gap. Fetch when the browser goes idle, which
      // on a normal visit is while the shopper is still near the top and the
      // swap is far below the viewport, where a height change costs nothing.
      const startLoad = () => this.loadProducts();
      if ('requestIdleCallback' in window) {
        requestIdleCallback(startLoad, { timeout: 3000 });
      } else {
        setTimeout(startLoad, 0);
      }

      // ...and pull the fetch forward if the shopper gets there first. Whichever
      // fires first wins; loadProducts() is guarded so the other is a no-op.
      if (!('IntersectionObserver' in window)) return;
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          this.loadProducts();
        },
        { rootMargin: '0px 0px 1200px 0px' }
      );
      // The section reserves its height above, so this element now has a real
      // box of its own. It still observes the section wrapper, which is the
      // node that stays in layout on every path including the no-history one.
      observer.observe(this.closest('.shopify-section') || this.parentElement || this);
    }

    // The exact set of cards this section will render, resolved synchronously
    // from localStorage. reserveSpace() and loadProducts() must agree on this
    // or the reserved box is the wrong size.
    usableHandles() {
      const currentHandle = normalizeHandle(this.dataset.currentProductHandle);
      const maxProducts = parseInt(this.dataset.productsToShow, 10) || 6;
      return readHandles()
        .filter((handle) => handle !== currentHandle)
        .slice(0, maxProducts);
    }

    // Which of the slider layouts a given card count resolves to. Extracted so
    // reserveSpace() and renderCards() cannot drift: the reserved box is only
    // the right height if it is laid out under the same classes the real cards
    // will get.
    layoutFor(count) {
      const columnsDesktop = parseInt(this.dataset.columnsDesktop, 10) || 6;
      const columnsMobile = parseInt(this.dataset.columnsMobile, 10) || 2;
      const showDesktopSlider =
        this.hasAttribute('data-enable-desktop-slider') && count > columnsDesktop;
      const showMobileSlider =
        this.hasAttribute('data-swipe-on-mobile') && count > columnsMobile;
      return {
        showDesktopSlider,
        showMobileSlider,
        isSlider: showDesktopSlider || showMobileSlider,
      };
    }

    // Mirror featured-collection's conditional slider classes. Idempotent, so
    // calling it again from renderCards() after reserveSpace() is harmless.
    applyLayout(layout) {
      if (layout.isSlider) this.grid.classList.add('slider');
      if (layout.showDesktopSlider) {
        this.grid.classList.add('slider--desktop');
        if (this.sliderComponent) {
          this.sliderComponent.classList.add('slider-component-desktop');
          this.sliderComponent.classList.remove('page-width-desktop');
        }
        if (this.titleWrapper) this.titleWrapper.classList.add('collection__title--desktop-slider');
      }
      if (layout.showMobileSlider) {
        this.grid.classList.add('slider--tablet', 'grid--peek');
        if (this.sliderComponent) this.sliderComponent.classList.remove('page-width');
        if (this.titleWrapper) this.titleWrapper.classList.add('title-wrapper--self-padded-tablet-down');
      }
      if (layout.isSlider && this.sliderButtons) {
        this.sliderButtons.hidden = false;
        // Parity with featured-collection ("Recommended For You"): when only
        // the mobile slider is active, the desktop grid doesn't slide, so the
        // strip has no job at >=990px — and overlaying it across the cards is
        // what hid the per-card image-slider arrows under the cursor.
        this.sliderButtons.classList.toggle('large-up-hide', !layout.showDesktopSlider);
      }
    }

    // One empty <li> per incoming card, laid out under the real classes, so the
    // browser computes the row/column/gap geometry itself rather than this file
    // doing the arithmetic. Returns false if no height could be measured.
    //
    // The <li>s are deliberately left empty: base.css hides empty a/ul/dl/div/
    // section/article/p/h1-h6, but NOT li, so these keep their box.
    reserveSpace() {
      const count = this.handles.length;
      const layout = this.layoutFor(count);
      this.applyLayout(layout);

      const fragment = document.createDocumentFragment();
      for (let index = 0; index < count; index += 1) {
        const item = document.createElement('li');
        item.className = `grid__item grid__item-${index + 1}${layout.isSlider ? ' slider__slide' : ''}`;
        item.setAttribute('data-rv-reserved', '');
        fragment.appendChild(item);
      }
      this.grid.appendChild(fragment);
      this.removeAttribute('hidden');

      // Measured only once the section is visible and the items are in the
      // document — the fallback reads an item's own width, which is zero while
      // the section is still display:none.
      const height = this.reservedCardHeight();
      if (height <= 0) {
        this.clearReservation();
        return false;
      }
      this.grid.querySelectorAll('[data-rv-reserved]').forEach((item) => {
        item.style.height = `${height}px`;
      });
      return true;
    }

    // Three readings, best first:
    //   1. what a card in THIS rail actually measured last time (exact),
    //   2. a card from another product grid on the page (~24px/row too tall),
    //   3. the portrait aspect ratio plus a meta allowance (rough).
    //
    // The rail only ever shows for a shopper who has already viewed another
    // product, so by the time it appears the cache is almost always populated
    // and reading 1 applies. Readings 2 and 3 cover the first ever render.
    reservedCardHeight() {
      const cached = readCachedCardHeight(this.gridWidth());
      if (cached > 0) return cached;

      const reference = document.querySelector('.product-grid .grid__item > .product-card-wrapper');
      if (reference) {
        const height = Math.round(reference.getBoundingClientRect().height);
        if (height > 0) return height;
      }
      const spacer = this.grid.querySelector('[data-rv-reserved]');
      const width = spacer ? spacer.getBoundingClientRect().width : 0;
      return width > 0 ? Math.round(width * 1.5 + META_ALLOWANCE) : 0;
    }

    gridWidth() {
      return Math.round(this.grid.getBoundingClientRect().width);
    }

    // Remember what a real card in this rail measured, so the next product page
    // reserves the exact height instead of an estimate. Runs after the cards
    // are in the document and laid out.
    cacheCardHeight() {
      const card = this.grid.querySelector('.product-card-wrapper');
      if (!card) return;
      const height = Math.round(card.getBoundingClientRect().height);
      const width = this.gridWidth();
      if (height > 0 && width > 0) writeCachedCardHeight(width, height);
    }

    clearReservation() {
      this.grid.querySelectorAll('[data-rv-reserved]').forEach((item) => item.remove());
      this.setAttribute('hidden', '');
    }

    async loadProducts() {
      if (this.loadStarted) return;
      this.loadStarted = true;

      const handles = this.handles || this.usableHandles();

      // No history: stay hidden on the storefront (editor keeps placeholders).
      if (!handles.length) return;

      const results = await Promise.allSettled(handles.map((handle) => this.fetchCard(handle)));
      const cards = results
        .filter((result) => result.status === 'fulfilled' && result.value)
        .map((result) => result.value);

      // Nothing came back. The space was reserved optimistically, so give it
      // back rather than leaving a titled empty block on the page.
      if (!cards.length) {
        this.clearReservation();
        return;
      }

      this.renderCards(cards);
    }

    async fetchCard(handle) {
      const response = await fetch(
        `${ROOT}products/${encodeURIComponent(handle)}?section_id=${CARD_SECTION_ID}`,
        { credentials: 'same-origin' }
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const text = await response.text();
      const wrapper = document.createElement('div');
      wrapper.innerHTML = text;
      return wrapper.querySelector('.product-card-wrapper');
    }

    renderCards(cards) {
      const sectionId = this.dataset.sectionId;
      const layout = this.layoutFor(cards.length);

      const fragment = document.createDocumentFragment();
      cards.forEach((card, index) => {
        const item = document.createElement('li');
        item.id = `Slide-${sectionId}-${index + 1}`;
        item.className = `grid__item grid__item-${index + 1}${layout.isSlider ? ' slider__slide' : ''}`;
        item.appendChild(card);
        fragment.appendChild(item);
      });

      // Replaces the reserved spacers in the same pass, so the row's height
      // never drops to zero in between.
      this.grid.innerHTML = '';
      this.grid.appendChild(fragment);

      this.applyLayout(layout);

      this.removeAttribute('hidden');

      if (this.sliderComponent && typeof this.sliderComponent.resetPages === 'function') {
        this.sliderComponent.resetPages();
      }

      this.cacheCardHeight();
    }
  }

  customElements.define('recently-viewed-products', RecentlyViewedProducts);
})();
